import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { FormSchemaProvider } from '@procureai/ui-form-mui';
import { ListContainer, useUuid } from '@procureai/ui-mui';
import { isNotEmpty } from 'ramda';
import { SubmitHandler, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { SupplierContactForm } from '~/routes/IntakeDetail/components/SupplierContactForm/SupplierContactForm';
import {
  BatchSupplierContact,
  batchSupplierContactSchema,
  supplierContactWithNameDefault,
} from '~/routes/IntakeDetail/components/SupplierContactForm/SupplierContactForm.schema';

export interface BatchAddSuppliersDialogProps {
  open: boolean;
  loading?: boolean;
  qualifiedSuppliers: { id: string; name: string }[];
  onClose: () => void;
  onSubmit: (data: BatchSupplierContact) => Promise<void>;
}

const SupplierListContent = () => {
  const { control } = useFormContext<BatchSupplierContact>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'suppliers',
  });

  return (
    <ListContainer
      items={fields}
      renderItem={({ index }) => <SupplierContactForm index={index} hideLocation />}
      getKey={(item) => item.id}
      onAddClick={() => append(supplierContactWithNameDefault)}
      onDeleteClick={(index) => remove(index)}
      deleteIconPosition="right"
    />
  );
};

export const BatchAddSuppliersDialog = ({
  open,
  loading,
  qualifiedSuppliers,
  onClose,
  onSubmit,
}: BatchAddSuppliersDialogProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandCaseListWidget.batchDialog' });
  const formId = useUuid();

  const defaultValues: BatchSupplierContact = {
    suppliers: isNotEmpty(qualifiedSuppliers)
      ? qualifiedSuppliers.map((supplier) => ({
          ...supplierContactWithNameDefault,
          supplier: { ...supplierContactWithNameDefault.supplier, name: supplier.name, internalId: supplier.id },
        }))
      : [supplierContactWithNameDefault],
  };

  const handleSubmit: SubmitHandler<BatchSupplierContact> = async (formData) => {
    await onSubmit(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('title')}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <FormSchemaProvider<BatchSupplierContact>
          id={formId}
          schema={batchSupplierContactSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
        >
          <SupplierListContent />
        </FormSchemaProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('button.cancel')}</Button>
        <Button loading={loading} disabled={loading} type="submit" form={formId} variant="contained" color="primary">
          {t('button.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
